import React, { Fragment } from 'react';
import styled from '@2pulse/responsive';
import { extractFormError, normalize, withComponents } from '@app/helpers';
import { row, worksansoxford } from '@app/style';
import { SvgBin } from '../../icons/SvgBin';
import SvgArrowDown from '../../icons/SvgArrowDown';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { WithSizesProps, WithTextProps } from '@app/types';
import { ColumnDef, flexRender, getCoreRowModel, Row, Table, useReactTable } from '@tanstack/react-table';
import { FlatList, FlatListProps, Modal, View } from 'react-native';
import { IBase } from '@app/common';
import { Button } from '../buttons/Button';
import { SvgMenuSearch } from '@app/components/icons';
import { isDefined, isFunction } from '@app/helpers/kindOf';
import { StyledInputs } from '@app/components/styled/form/Inputs';
import { StyledForms } from '@app/components/styled/form/Form';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement, restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities'
import { SvgSwitch } from '@app/components/icons/SvgSwitch';
import { useVirtualizer } from '@tanstack/react-virtual'
import SvgDragAndDrop from '@app/components/icons/SvgDragAndDrop';
import { HelperText } from '@app/components/styled/form/Label';

const ListHeader = styled.View({
  ...row,
  paddingBottom: normalize(21),
  paddingRight: normalize(34),
});

const ListHeaderItem = styled.Text<WithSizesProps & WithTextProps>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  flexShrink: 0,
  flexGrow: 0,
  width: props.width,
  color: theme.colors.babypowder,
  opacity: 0.8,
  fontSize: normalize(18),
  textAlign: props.align,
  paddingLeft: normalize(34),
}));

const ListItem = styled.TouchableOpacity<{ isActive?: boolean, compact?: boolean }>(({ theme, props }) => ({
  ...row,
  alignItems: 'center',
  height: props.compact ? 55 : 80,
  marginBottom: 2,
  paddingRight: normalize(34),
  backgroundColor: props.isActive ? theme.colors.celadonblue : theme.colors.translucid,
}));

const ListItemView = styled.View<{ isActive?: boolean, compact?: boolean }>(({ theme, props }) => ({
  ...row,
  alignItems: 'center',
  height: props.compact ? 55 : 80,
  marginBottom: 2,
  overflow: "hidden",
  paddingRight: normalize(34),
  backgroundColor: props.isActive ? theme.colors.celadonblue : theme.colors.translucid,
}));

const ListItemCol = styled.View<WithSizesProps>(({ props }) => ({
  flexShrink: 0,
  flexGrow: 0,
  width: props.width,
  paddingLeft: normalize(34),
}));

const ListBody = styled.View({
  width: "100%",
  height: '100%',
  // paddingHorizontal: '21%',
});

const ListSearch = styled.View({
  width: "100%",
  height: normalize(58),
  flexDirection: 'row',
  alignItems: 'center',
  gap: 13,
  marginBottom: normalize(13),
  paddingBottom: normalize(13),
});
const ListSearchIcon = styled.View({
  height: 34,
});

const ListItemText = styled.Text<WithTextProps>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  color: theme.colors.babypowder,
  fontSize: normalize(16),
  textAlign: props.align,
  fontWeight: props.bold ? '700' : '400',
}));

const SelectContainer = styled.View<{ small?: boolean, onWhite?: boolean }>(({ props }) => ({
  ...row,
  height: props.onWhite ? 34 : props.small ? 58 : 76,
}));

const SelectTrigger = styled.TouchableOpacity<{ small?: boolean, center?: boolean, inside?: boolean, deletable?: boolean, bordered?: boolean, onWhite?: boolean }>(({ theme, props }) => ({
  ...row,
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  flexGrow: 1,
  borderRadius: props.onWhite ? 22 : props.inside ? 0 : 5,
  borderTopRightRadius: props.onWhite ? 22 : props.deletable ? 0 : 5,
  borderBottomRightRadius: props.onWhite ? 22 : props.deletable ? 0 : 5,
  backgroundColor: props.onWhite
    ? (props.small ? '#f8f9fa' : '#fff')
    : (props.small ? theme.colors.oxfordblue : 'rgba(240, 249 , 252, .04)'),
  paddingLeft: props.center ? 0 : props.small ? normalize(21) : normalize(34),
  paddingRight: props.center ? 0 : props.small ? normalize(21) : normalize(21),
  alignItems: 'center',
  justifyContent: props.center ? 'center' : 'space-between',
  borderWidth: props.bordered ? normalize(2) : 0,
  borderColor: props.onWhite ? theme.colors.celadonblue : "#00BEE6",
  borderRightWidth: props.deletable ? 0 : normalize(2),
  borderRightColor: props.bordered ? (props.onWhite ? theme.colors.celadonblue : "#00BEE6") : "transparent",
}));

const SelectLabel = styled.Text<{ onWhite?: boolean }>(({ theme, props }) => ({
  ...worksansoxford,
  fontSize: props.onWhite ? normalize(16) : normalize(21),
  fontWeight: '500',
  color: props.onWhite ? '#212529' : theme.colors.babypowder,
}));

const SelectToggle = styled.View<{ onWhite?: boolean }>(({ props }) => ({
  width: props.onWhite ? normalize(18) : normalize(34),
  height: props.onWhite ? normalize(18) : normalize(34),
  marginLeft: props.onWhite ? normalize(8) : 0,
}));


const SelectDelete = styled.TouchableOpacity<{ inside?: boolean, bordered?: boolean, onWhite?: boolean }>(({ theme, props }) => ({
  width: normalize(21 + 34 + 21),
  paddingHorizontal: normalize(21),
  height: '100%',
  borderTopRightRadius: props.inside ? 0 : 5,
  borderBottomRightRadius: props.inside ? 0 : 5,
  backgroundColor: props.onWhite ? 'rgba(0, 0, 0, 0.05)' : 'rgba(240, 249 , 252, .04)',
  borderWidth: props.bordered ? normalize(2) : 0,
  borderColor: props.onWhite ? "#6c757d" : "#00BEE6",
  borderLeftWidth: normalize(1),
  borderLeftColor: props.onWhite ? '#6c757d' : theme.colors.oxfordblue,
}));

type SelectOptionProps = {
  isOpen: boolean;
};

export const SelectOptionsContainer = styled.View<SelectOptionProps>(({ props }) => ({
  display: props.isOpen ? 'flex' : 'none',
  zIndex: 999999,
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  paddingVertical: '13%',
  backgroundColor: 'rgba(2,24,43,1)',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const SelectOptionsTitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  color: theme.colors.babypowder,
  fontSize: normalize(55),
  fontWeight: '800',
  paddingBottom: normalize(55),
}));

export const SelectOptionsSubTitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  color: theme.colors.babypowder,
  fontSize: normalize(21),
  fontWeight: '400',
  alignSelf: 'flex-start',
  paddingBottom: normalize(21),
  // textAlign: 'center',
}));

export const SelectOptionsMessage = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  color: theme.colors.babypowder,
  fontSize: normalize(18),
  fontWeight: '600',
  textAlign: 'center',
  lineHeight: normalize(28),
}));

export const SelectOptions = styled.ScrollView({
  display: 'flex',
  width: '55%',
  maxHeight: '55%',
  height: 'auto',
  flexGrow: 0,
  overflow: 'hidden'
});

export const SelectOptionsCols = styled.View({
  display: 'flex',
  flexDirection: 'row',
  maxHeight: '100%',
  // height: '100%',
  width: '100%',
  // backgroundColor: 'rgba(255,255,255,0.2)',
  justifyContent: 'center',
  paddingHorizontal: '13%',
  overflow: 'hidden',
  paddingBottom: normalize(34),
});

export const SelectOptionsResults = styled.View({
  flexGrow: 1,
  alignItems: 'center',
  paddingRight: '8%',
});

export const SelectOptionsSelection = styled.View({
  flexGrow: 1,
  maxWidth: '50%',
  alignItems: 'center',
  justifyContent: 'center',
});

export const SelectOptionsSelectionList = styled.ScrollView({
  // flexGrow: 1,
  width: '100%',
  gap: normalize(2),
});

export const SelectOption = styled.TouchableOpacity(({ theme }) => ({
  fontFamily: 'Work Sans',
  backgroundColor: theme.colors.babypowder,
  borderRadius: normalize(5),
  marginVertical: normalize(13),
  height: normalize(60),
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const SelectOptionLabel = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  color: theme.colors.oxfordblue,
  fontSize: normalize(18),
  fontWeight: '600',
}));

export const SelectOptionsClose = styled.TouchableOpacity({});

export const SelectOptionsCloseLabel = styled.Text({
  marginTop: normalize(34),
  height: 67,
});

export interface BaseSelectProps<T> extends Omit<FlatListProps<T>, 'data' | 'keyExtractor' | 'renderItem'> {
  name: string;
  table: Table<T>;
  label?: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  formattedLabel: string;
  onReset?: () => void,
  onMove?: (fromIndex: number, toIndex: number) => void;
  onSelect: (item: T) => void;
  isActive: (item: T) => boolean;
  transform?: {
    value?: (item: T) => any; // Transform the value for field.onChange
    accessor?: (item: any) => any; // How to access to the field value
    display?: (item: any) => any; // How to display the "name" of the field
  }
  onRemove?: (item: any) => void;
  onSearch?: (search: string) => void;
  render: (item: T, index: number) => any
  center?: boolean;
  inside?: boolean;
  bordered?: boolean;
  small?: boolean;
  values: any[];
  helper?: string;
  selectedColumns?: ColumnDef<any, any>[];
  onWhite?: boolean;
  hidden?: boolean;
}

const modifiers = [restrictToVerticalAxis]; // restrictToParentElement

type UseSortableReturn = Omit<
  ReturnType<typeof useSortable>,
  'setNodeRef' | 'transform' | 'transition'
>

function SortableItem(props: {
  id: string
  children: (args: UseSortableReturn) => React.ReactNode
}) {
  const {
    setNodeRef,
    transform,
    transition,
    ...rest
  } = useSortable({
    id: props.id,
    // disabled: true
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  return (
    <div ref={setNodeRef} style={style}>
      {props.children({ ...rest })}
    </div>
  )
}

export const BaseSelect = withComponents(<T extends IBase,>({
  name,
  isOpen,
  setIsOpen,
  isActive,
  onSelect,
  onSearch,
  onReset,
  render,
  onMove,
  formattedLabel,
  label,
  table,
  values,
  selectedColumns,
  onRemove = (item: T) => { },
  helper,
  onWhite = false,
  hidden = false,
  ...props
}: BaseSelectProps<T>) => {
  const { formState } = useFormContext();

  const error = extractFormError(formState.errors, name);

  const toggle = () => {
    setIsOpen((prev) => !prev);
  }

  const onClose = () => {
    setIsOpen(false);
  }

  const parentRef = React.useRef<HTMLDivElement>(null)

  const { rows } = table.getRowModel()

  const tableSelected = useReactTable({
    data: values,
    columns: selectedColumns || [],
    getCoreRowModel: getCoreRowModel(),
    getRowId(originalRow) {
      return originalRow.id;
    },
  });

  // https://tanstack.com/virtual/latest/docs/api/virtualizer
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 34,
    overscan: 20,
  })

  // TODO: Update all selects
  const isLegacy = !isDefined(selectedColumns);

  return (
    <SelectContainer {...props} onWhite={onWhite}>

      {error && (
        <StyledForms.Error>
          <StyledForms.ErrorLabel>
            {error?.message || "Ce champ est invalide"}
          </StyledForms.ErrorLabel>
        </StyledForms.Error>
      )}

      {!hidden && (
        <SelectTrigger onPress={toggle} onWhite={onWhite} {...props}>
          <SelectLabel onWhite={onWhite}>{formattedLabel}</SelectLabel>
          <SelectToggle onWhite={onWhite}>
            <SvgArrowDown color={onWhite ? "#212529" : "#fff"} />
          </SelectToggle>
        </SelectTrigger>
      )}

      <Modal visible={isOpen} >

        <SelectOptionsContainer isOpen={isOpen}>

          <SelectOptionsTitle>{label}</SelectOptionsTitle>
          <SelectOptionsCols>
            <SelectOptionsResults>
              <ListBody>

                {isFunction(onSearch) && (
                  <ListSearch>
                    <ListSearchIcon><SvgMenuSearch strokePath='#FFF' /></ListSearchIcon>
                    <StyledInputs.Holder>
                      <StyledInputs.Input
                        size='small'
                        placeholder="Cherchez dans la liste..."
                        onChangeText={onSearch}
                        placeholderTextColor="white"
                        autoCapitalize="none" />
                    </StyledInputs.Holder>
                  </ListSearch>
                )}

                <ListHeader>
                  {table.getFlatHeaders().map(header => (
                    <React.Fragment key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </React.Fragment>
                  ))}
                </ListHeader>

                {Boolean(table.getRowModel().rows.length) ? (
                  <FlatList
                    {...props as any}
                    onEndReachedThreshold={1}
                    data={table.getRowModel().rows}
                    keyExtractor={(item: Row<T>) => item.id}
                    renderItem={({ item }: { item: Row<T> }) => {
                      return <ListItem isActive={isActive(item.original)} onPress={() => onSelect(item.original)}>
                        {item.getVisibleCells().map(cell => (
                          <React.Fragment key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </React.Fragment>
                        ))}
                      </ListItem>
                    }}
                  />
                ) : (
                  <SelectOptionsMessage>
                    Aucun résultat
                  </SelectOptionsMessage>
                )}

              </ListBody>
            </SelectOptionsResults>

            <SelectOptionsSelection>
              <SelectOptionsSubTitle>Votre sélection</SelectOptionsSubTitle>

              {!values.length && (
                <SelectOptionsMessage>
                  Vous n'avez pas encore choisi d'éléments dans la liste de gauche.
                </SelectOptionsMessage>
              )}

              {isLegacy
                ? (
                  <SelectOptionsSelectionList>
                    {values.map((item: T & { id: string }, index) => render(item, index))}
                  </SelectOptionsSelectionList>
                ) : (
                  <Fragment>

                    {!!values.length && (
                      <View style={{ width: '100%' }}>
                        <ListHeader>
                          {tableSelected.getFlatHeaders().map(header => (
                            <React.Fragment key={header.id}>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </React.Fragment>
                          ))}
                        </ListHeader>
                      </View>
                    )}

                    <SelectOptionsSelectionList>
                      <DndContext
                        modifiers={modifiers}
                        onDragEnd={(event) => {
                          const { active, over } = event

                          if (over && active.id !== over.id) {
                            const activeIndex = active.data.current?.sortable?.index;
                            const overIndex = over.data.current?.sortable?.index;

                            if (onMove && activeIndex !== undefined && overIndex !== undefined) {
                              onMove(activeIndex, overIndex)
                            }
                          }
                        }}>
                        <SortableContext items={tableSelected.getRowModel().rows}>
                          <FlatList
                            {...props as any}
                            onEndReachedThreshold={1}
                            data={tableSelected.getRowModel().rows}
                            keyExtractor={(item: Row<T>) => item.id}
                            renderItem={({ item }: { item: Row<T> }) => (
                              <SortableItem id={item.id}>
                                {({ attributes, listeners, active }) => (
                                  <ListItemView isActive compact={false}>
                                    <View style={{ paddingLeft: 8 }}>
                                      <Button onPress={() => onRemove(item.original)} title='X' style={{ backgroundColor: 'transparent' }} />
                                    </View>

                                    {item.getVisibleCells().map(cell => (
                                      <React.Fragment key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, { ...cell.getContext(), onRemove })}
                                      </React.Fragment>
                                    ))}

                                    {/* <View
                                      style={{
                                        width: 24,
                                        height: 24,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        display: 'flex',
                                        cursor: active ? "grabbing" : "grab"
                                      }}>
                                      <SvgDragAndDrop
                                        {...listeners}
                                        {...attributes}
                                        color="#fff" />
                                    </View> */}

                                  </ListItemView>
                                )}
                              </SortableItem>
                            )}
                          />
                        </SortableContext>
                      </DndContext>
                    </SelectOptionsSelectionList>
                  </Fragment>
                )}

            </SelectOptionsSelection>

          </SelectOptionsCols>

          <SelectOptionsClose>
            <SelectOptionsCloseLabel>
              <Button title="Fermer" grow large bordered onPress={onClose} />
            </SelectOptionsCloseLabel>
          </SelectOptionsClose>

        </SelectOptionsContainer>
      </Modal>

      {isFunction(onReset) && (
        <SelectDelete onPress={onReset} onWhite={onWhite} {...props}>
          <SvgBin color={onWhite ? "#212529" : "#fff"} />
        </SelectDelete>
      )}
    </SelectContainer >
  );
}, {
  ListBody,
  ListHeader,
  ListHeaderItem,
  ListItem,
  ListItemCol,
  ListItemText
});
